import React, { DragEvent, useState } from 'react';
import styled, { css } from 'styled-components';
import { CameraIcon } from '../../assets';
import { BreakpointsOfTOrT, theme } from '../../theme';
import { useBreakpointMap } from '../../utils';
import { MediaCard, MediaCardProps } from '../cards';
import { Divider } from '../divider';

const Container = styled.div<{ isDragged: boolean }>`
  border: 1px dashed ${theme.colors.neutrals.solidGrays[5]};
  transition: border 0.2s ease-in;
  ${({ isDragged }) =>
    isDragged &&
    css`
      border: 2px dashed ${theme.colors.primary.graysBlue};
    `}
  border-radius: ${theme.borderRadius.default};
  padding: ${theme.spacings[6].rem};
  text-align: center;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, 100px);
  grid-gap: ${theme.spacings[3].rem};
  justify-content: center;
`;

const EmptyBox = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  border: 1px dashed ${theme.colors.neutrals.solidGrays[5]};
  border-radius: ${theme.borderRadius.small};
  width: 100%;
  height: 100%;
`;

const Camera = styled(CameraIcon)`
  fill: ${theme.colors.primary.graysBlue};
`;

const CameraText = styled.strong<{ mappedSize: MediaUploadSize }>`
  ${({ mappedSize }) =>
    mappedSize === 'large'
      ? theme.typography.body.lg
      : theme.typography.body.sm};

  ${({ mappedSize }) =>
    mappedSize === 'large' && `padding-top: ${theme.spacings[4].rem}`};

  color: ${theme.colors.primary.graysBlue};
  font-weight: 700;
  display: block;
  max-width: 200px;
  margin: 0 auto;
`;

export type MediaUploadSize = 'small' | 'large';

export type MediaUploadProps = {
  mediaCards: MediaCardProps[];
  size?: BreakpointsOfTOrT<MediaUploadSize>;
  onDragOver: (e: DragEvent<HTMLDivElement>) => void;
  onDragEnter: (e: DragEvent<HTMLDivElement>) => void;
  onDragLeave: (e: DragEvent<HTMLDivElement>) => void;
  onDrop: (e: DragEvent<HTMLDivElement>) => void;
};

export const MediaUpload: React.FC<MediaUploadProps> = ({
  mediaCards,
  size = {
    xs: 'small',
    md: 'large',
  },
  onDragOver,
  onDragEnter,
  onDragLeave,
  onDrop,
}) => {
  const [isDragged, setIsDragged] = useState(false);
  const mappedSize = useBreakpointMap(size);

  const dragEnter = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragged(true);
    onDragEnter(e);
  };

  const dragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragged(false);
    onDragLeave(e);
  };

  return mappedSize === 'large' ? (
    <Container
      onDragOver={onDragOver}
      onDragEnter={dragEnter}
      onDragLeave={dragLeave}
      onDrop={onDrop}
      isDragged={isDragged}
    >
      <Grid>
        {mediaCards.map((card) => (
          <MediaCard key={card.id} {...card} />
        ))}
      </Grid>
      <Divider />
      <Camera width={80} />
      <CameraText mappedSize={mappedSize}>
        Drag and drop images or click to upload
      </CameraText>
    </Container>
  ) : (
    <Grid>
      {mediaCards.map((card) => (
        <MediaCard key={card.id} {...card} />
      ))}
      <EmptyBox>
        <Camera width={40} />
        <CameraText mappedSize={mappedSize}>Add photos</CameraText>
      </EmptyBox>
    </Grid>
  );
};
